import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Activity, 
  Search, 
  FileText, 
  Building2, 
  CreditCard, 
  Mail,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

export default function LandingPage({ onShowAuth, onNavigate }) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Show message and navigate to auth
      toast.info('Please create an account or sign in to search healthcare costs');
      onShowAuth();
    } else {
      toast.error('Please enter a procedure name or CPT code');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/kj0ydf6o_HCD-1.png"
                alt="Healthcare Dollar Logo"
                className="h-12 w-auto"
              />
            </div>

            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#home" className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors">
                Home
              </a>
              <a href="#search" className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors">
                Search
              </a>
              <a href="#browse-procedure" className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors">
                Browse by Procedure
              </a>
              <a href="#browse-provider" className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors">
                Browse by Provider
              </a>
              <a href="#browse-payer" className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors">
                Browse by Payer
              </a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors">
                Get in Touch
              </a>
              <Button
                onClick={onShowAuth}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                Log In
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <Button
              onClick={onShowAuth}
              size="sm"
              className="md:hidden bg-green-500 hover:bg-green-600"
            >
              Log In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative bg-gradient-to-r from-green-600 to-emerald-600 text-white overflow-hidden py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/m2emq3ly_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.2,
          imageRendering: '-webkit-optimize-contrast'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-3xl mb-6 px-6 py-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/kj0ydf6o_HCD-1.png"
              alt="Healthcare Dollar Logo"
              className="h-16 w-auto"
            />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
            Increasing consumer knowledge.<br />Enhancing consumer control.
          </h1>
          
          <p className="text-2xl md:text-3xl mb-6 text-green-100 font-light">
            Consumer Knowledge = Consumer Control
          </p>
          
          <p className="text-xl mb-8 text-green-50">
            Healthcare Dollar is cost estimation made easy!
          </p>
          
          <Button
            onClick={() => document.getElementById('search').scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            className="bg-white text-green-600 hover:bg-green-50 text-lg px-8 py-6 gap-2"
          >
            Search Now
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Search Section */}
      <section id="search" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Search Healthcare Costs
            </h2>
            <p className="text-lg text-gray-600">
              Find cost estimates for procedures, providers, or by your insurance plan
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-3xl mx-auto mb-16">
            <form onSubmit={handleSearch} className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by procedure name or CPT code"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 text-lg border-2 border-gray-300 focus:border-green-500"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 px-8"
                >
                  Search
                </Button>
              </div>
            </form>
          </div>

          {/* Browse Options */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Browse by Procedure */}
            <Card className="border-2 border-green-100 hover:border-green-300 hover:shadow-lg transition-all cursor-pointer group">
              <CardHeader>
                <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Browse by Procedure</CardTitle>
                <CardDescription>
                  Search for cost estimates by medical procedure type
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={onShowAuth}
                  variant="link"
                  className="text-green-600 p-0 gap-2 group-hover:gap-3 transition-all"
                >
                  View More
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Browse by Provider */}
            <Card className="border-2 border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Browse by Provider</CardTitle>
                <CardDescription>
                  Compare costs across different healthcare providers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={onShowAuth}
                  variant="link"
                  className="text-blue-600 p-0 gap-2 group-hover:gap-3 transition-all"
                >
                  View More
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Browse by Payer */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-lg transition-all cursor-pointer group">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <CreditCard className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">Browse by Payer</CardTitle>
                <CardDescription>
                  View costs based on your insurance carrier
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={onShowAuth}
                  variant="link"
                  className="text-purple-600 p-0 gap-2 group-hover:gap-3 transition-all"
                >
                  View More
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Content */}
            <div>
              <Badge className="bg-green-500 hover:bg-green-600 text-white mb-4">
                Why Healthcare Dollar?
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Consumers need a cost estimation tool
              </h2>
              <div className="space-y-4 text-lg text-gray-600 mb-8">
                <p>
                  Use estimated cost information to budget for elective medical procedures for you and your family.
                </p>
                <p className="font-semibold text-green-700">
                  Healthcare Dollar puts the consumer in control!
                </p>
              </div>

              {/* Feature List */}
              <div className="space-y-3 max-w-2xl mx-auto text-left">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Personalized cost estimates based on your insurance</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Compare providers and find the best value</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Transparent pricing - no surprise bills</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Make informed healthcare decisions with confidence</p>
                </div>
              </div>

              <Button
                onClick={onShowAuth}
                size="lg"
                className="mt-8 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 gap-2"
              >
                Get Started Now
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-500 hover:bg-blue-600 text-white mb-4">
              Our Platform
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Know Before You Owe
            </h2>
            <p className="text-xl text-gray-600">
              Empowering Patients Through Financial Clarity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center mb-4">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Real-Time Cost Estimates</h3>
              <p className="text-gray-600">
                Personalized out-of-pocket projections for elective procedures, tailored to your insurance plan.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Insurance Decoder</h3>
              <p className="text-gray-600">
                Breaks down deductibles, copays, coinsurance, and coverage status in plain language.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-purple-500 rounded-2xl flex items-center justify-center mb-4">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Provider Comparison</h3>
              <p className="text-gray-600">
                See how prices vary across hospitals, surgery centers, and office-based settings.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Financial Navigation</h3>
              <p className="text-gray-600">
                Step-by-step guidance to avoid surprise bills and maximize coverage.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-teal-50 to-cyan-50 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Equity-Driven Design</h3>
              <p className="text-gray-600">
                Built for transparency, accessibility, and trust — especially for underserved communities.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center mb-4">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Grant-Ready Analytics</h3>
              <p className="text-gray-600">
                Tracks cost disparities, billing trends, and consumer impact for funders and partners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-green-600 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Get in Touch</h2>
          <p className="text-xl text-green-100 mb-8">
            Have questions about healthcare costs? We're here to help you make informed decisions.
          </p>
          <a href="mailto:info@healthcaredollar.tech?subject=Healthcare Dollar Inquiry">
            <Button
              size="lg"
              className="bg-white text-green-600 hover:bg-green-50 text-lg px-8 py-6"
            >
              Contact Us
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/kj0ydf6o_HCD-1.png"
                  alt="Healthcare Dollar Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-sm">
                Empowering patients through transparent healthcare pricing and cost estimation.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="#home" className="block hover:text-green-400 transition-colors">Home</a>
                <a href="#search" className="block hover:text-green-400 transition-colors">Search</a>
                <a href="#contact" className="block hover:text-green-400 transition-colors">Contact</a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <div className="space-y-2 text-sm">
                <a href="/privacy-policy" className="block hover:text-green-400 transition-colors">Privacy Policy</a>
                <a href="/terms-of-use" className="block hover:text-green-400 transition-colors">Terms of Use</a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>© 2025 Healthcare Dollar. All rights reserved.</p>
            <p className="mt-2 text-gray-500">Designed for transparency and trust</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
